import { GQLHeroBannerFragment } from '@theprintbar/graphql';
import { BrandColours, ImageSize, cn } from '@theprintbar/shared';
import {
	ButtonSize,
	CTAText,
	Carousel,
	CarouselNextButton,
	CarouselPreviousButton,
	FullCTA,
	FullCTATitleClass,
	OutlineLinkButton,
	PromoImageContainer,
	SmallArrowIcon,
} from '@theprintbar/ui';

interface HeroProps {
	content: GQLHeroBannerFragment;
	//First block on the page, so its first image is above the fold and should load eagerly
	isHero?: boolean;
}

const carouselKey = 'hero-banner';

export function HeroBanner({ content, isHero }: HeroProps) {
	const heroSlides = content.heroBanner.structure;
	if (!heroSlides?.length) return null;

	return (
		<section>
			{heroSlides.length === 1 ? (
				<FullCTA cta={{ structure: heroSlides[0] }} lazyLoad={!isHero} />
			) : (
				<Carousel
					carouselKey={carouselKey}
					slidesPerView="auto"
					loop={true}
					autoplay={{ delay: 8000, pauseOnMouseEnter: true }}
					slides={heroSlides.map((cta, index) =>
						cta
							? {
									containerProps: {
										className: cn(
											'px-2 lg:px-4 first:pl-4 last:pr-4 lg:first:pl-8 lg:last:pr-8 my-4 lg:my-8 z-40 max-w-7xl',
										),
										style: { height: 'auto', width: '95vw' },
									},
									content: (
										<>
											{/* Pill */}
											<div className="block lg:hidden uppercase rounded-full text-xs lg:text-sm px-1 py-0.5 lg:px-2 lg:py-1 border border-blue bg-yellow absolute top-0 -translate-y-1/2 right-8 lg:right-12 z-10">
												<div className="flex items-center">
													<h5 className="font-belfast">swipe for more</h5>
													<img loading="lazy" src="/favicon.gif" className="h-3 w-3 ml-1" alt="brandstar icon" />
												</div>
											</div>

											<div
												className="relative transition-all flex flex-col h-full lg:flex-row-reverse rounded-3xl border border-blue overflow-hidden"
												style={{ backgroundColor: cta.colour || BrandColours.BrightBlue }}
											>
												{/* Main Image */}
												<div className="lg:w-1/2 w-full lg:h-auto aspect-[4/3]">
													<PromoImageContainer
														size={ImageSize.XLarge}
														lazyLoad={!isHero || index > 0}
														sizes="(min-width: 1024px) 640px, 95vw"
														promoImage={cta?.image}
														imageClass="lg:aspect-square"
														className="w-full h-full "
													/>
												</div>
												{/* Content */}
												<div className="lg:w-1/2 flex-1 px-6 py-4 lg:p-14 flex flex-col justify-between gap-4 lg:gap-10 border-blue border-t lg:border-r lg:border-t-0 ">
													<div>
														{/* Brand Star Icon */}
														<img
															loading="lazy"
															src="/favicon.gif"
															alt="brand star icon"
															className="h-6 w-6 lg:h-8 lg:w-8 fill-blue mb-4 lg:mb-10"
														/>
														<div className="space-y-4 lg:space-y-10">
															<h2 className={FullCTATitleClass(cta.title)}>{cta.title}</h2>
															<CTAText
																useH3
																className="lg:text-lg text-base"
																text={cta.text?.content?.document?.[0]?.children?.[0]?.text}
															/>
															<div className="flex items-center flex-wrap gap-2 lg:gap-2.5">
																{cta.links?.map((link, index) =>
																	link ? (
																		<OutlineLinkButton
																			className="w-fit uppercase font-belfast !font-bold text-xs px-2 lg:px-2.5"
																			to={link.to || '#'}
																			icon={SmallArrowIcon}
																			iconSize={ButtonSize.S}
																			key={index}
																		>
																			{link.text}
																		</OutlineLinkButton>
																	) : null,
																)}
															</div>
														</div>
													</div>
													{/* Carousel Buttons */}
													{heroSlides.length > 1 && (
														<div className="mt-auto flex space-x-8">
															<CarouselPreviousButton carouselKey={carouselKey} />
															<CarouselNextButton carouselKey={carouselKey} />
														</div>
													)}
												</div>
											</div>
										</>
									),
							  }
							: null,
					)}
				/>
			)}
		</section>
	);
}

export default HeroBanner;
